import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash, Binoculars } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SECTORS } from "@/lib/prospect";
import { CampaignBoard } from "@/components/prospect/CampaignBoard";
import { ProspectStats } from "@/components/prospect/ProspectStats";
import { ProspectSettings } from "@/components/prospect/ProspectSettings";

function NewCampaign({ open, onClose, onCreated }) {
  const [f, setF] = useState({ country: "Cabo Verde", city: "Praia", sector: "restaurantes", max_results: 5 });
  const save = async () => {
    try {
      const { data } = await api.post("/prospect/campaigns", { ...f, max_results: Number(f.max_results) });
      onCreated(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-campaign-dialog">
        <DialogHeader><DialogTitle>Nova campanha</DialogTitle></DialogHeader>
        <div className="grid gap-3">
          <Label>País</Label>
          <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v })}>
            <SelectTrigger data-testid="campaign-country"><SelectValue /></SelectTrigger>
            <SelectContent>{["Cabo Verde", "Portugal"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Label>Cidade ou ilha</Label>
          <Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="campaign-city" />
          <Label>Setor</Label>
          <Input list="sectors" value={f.sector} onChange={(e) => setF({ ...f, sector: e.target.value })} data-testid="campaign-sector" />
          <datalist id="sectors">{SECTORS.map((s) => <option key={s} value={s} />)}</datalist>
          <Label>Número máximo de negócios</Label>
          <Input type="number" min={1} max={60} value={f.max_results} onChange={(e) => setF({ ...f, max_results: e.target.value })} data-testid="campaign-max" />
        </div>
        <DialogFooter><Button className="rounded-full" onClick={save} data-testid="campaign-create-btn">Criar campanha</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CampaignList({ onOpen }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/prospect/campaigns").then(({ data }) => setList(data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const remove = async (c) => {
    if (!window.confirm(`Apagar a campanha "${c.name}" e os seus negócios?`)) return;
    try { await api.delete(`/prospect/campaigns/${c.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4">
      <Button className="rounded-full" onClick={() => setOpen(true)} data-testid="new-campaign-btn"><Plus className="mr-1.5" /> Nova campanha</Button>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {list.map((c) => (
          <div key={c.id} className="flex items-start justify-between rounded-2xl border border-border bg-card p-4" data-testid={`campaign-card-${c.id}`}>
            <button className="text-left" onClick={() => onOpen(c.id)} data-testid={`campaign-open-${c.id}`}>
              <div className="font-display font-bold">{c.name}</div>
              <div className="text-sm text-muted-foreground">{c.count} negócios · máx. {c.max_results}</div>
            </button>
            <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`campaign-delete-${c.id}`}><Trash /></Button>
          </div>
        ))}
        {!list.length && <p className="text-sm text-muted-foreground" data-testid="campaigns-empty">Ainda não há campanhas.</p>}
      </div>
      <NewCampaign open={open} onClose={() => setOpen(false)} onCreated={(c) => { setOpen(false); onOpen(c.id); }} />
    </div>
  );
}

export default function Prospecting() {
  const [cid, setCid] = useState(null);
  return (
    <div className="space-y-6" data-testid="prospecting-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild><Link to="/plataforma"><ArrowLeft /></Link></Button>
        <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold"><Binoculars className="text-primary" /> Prospeção</h1>
      </div>
      <Tabs defaultValue="campanhas">
        <TabsList>
          <TabsTrigger value="campanhas" data-testid="tab-campaigns">Campanhas</TabsTrigger>
          <TabsTrigger value="painel" data-testid="tab-stats">Painel</TabsTrigger>
          <TabsTrigger value="definicoes" data-testid="tab-settings">Definições</TabsTrigger>
        </TabsList>
        <TabsContent value="campanhas" className="pt-4">
          {cid ? <CampaignBoard cid={cid} onBack={() => setCid(null)} /> : <CampaignList onOpen={setCid} />}
        </TabsContent>
        <TabsContent value="painel" className="pt-4"><ProspectStats /></TabsContent>
        <TabsContent value="definicoes" className="pt-4"><ProspectSettings /></TabsContent>
      </Tabs>
    </div>
  );
}
